// src/pages/auth/SignUpPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../../services/auth.service';
import logonegro from '../../assets/images/logonegro.svg';
import fondoPantalla from '../../assets/images/fondo_pantalla.svg';
import '../../assets/styles/auth-common.css';
import Footer from '../../components/layout/footer';
import { SignUpForm, PasswordValidation } from '../../types/auth.types';
import { useWindow } from '../../context/WindowContext';

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { windowWidth, isMobile } = useWindow();
  
  // Capturar código de referido del URL (query params o path)
  const pathParts = window.location.pathname.split('/');
  const pathReferralCode = pathParts.find(part => part.match(/^[A-Z0-9]{6,}$/));
  const queryReferralCode = searchParams.get('ref');
  const referralCode = queryReferralCode || pathReferralCode || null;
  const [formData, setFormData] = useState<SignUpForm>({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState<'EN' | 'ES'>('EN');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    hasLower: false,
    hasUpper: false,
    hasNumber: false,
    hasSpecial: false,
    isValid: false
  });
  const [hasReferralCode, setHasReferralCode] = useState(false);
  const [referrerName, setReferrerName] = useState<string | null>(null);

  // Efecto para verificar código de referido y evitar redirección automática
  useEffect(() => {
    if (referralCode) {
      setHasReferralCode(true);
      console.log('Referral code detected:', referralCode);
      // Guardar el código en localStorage para Subutai
      localStorage.setItem('pendingReferralCode', referralCode);
      
      // Si el usuario está autenticado, hacer logout para permitir nuevo registro
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (token) {
        console.log('User authenticated, clearing session to allow new registration with referral');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        // Forzar recarga para limpiar el estado de autenticación
        window.location.reload();
      }
    }
  }, [referralCode]);

  useEffect(() => {
    if (formData.confirmPassword && formData.password !== formData.confirmPassword) {
      setPasswordsMatch(false);
    } else {
      setPasswordsMatch(true);
    }

    const validation = {
      hasLower: /[a-z]/.test(formData.password),
      hasUpper: /[A-Z]/.test(formData.password),
      hasNumber: /[0-9]/.test(formData.password),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(formData.password),
      isValid: false
    };

    validation.isValid =
      validation.hasLower &&
      validation.hasUpper &&
      validation.hasNumber &&
      validation.hasSpecial;

    setPasswordValidation(validation);
  }, [formData.password, formData.confirmPassword]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const renderPasswordRequirements = () => (
    <div className="password-requirements">
      <h4>Password requirements:</h4>
      <ul>
        <li className={passwordValidation.hasLower ? 'valid' : 'invalid'}>
          {passwordValidation.hasLower ? '✓' : '○'} Lowercase letter
        </li>
        <li className={passwordValidation.hasUpper ? 'valid' : 'invalid'}>
          {passwordValidation.hasUpper ? '✓' : '○'} Uppercase letter
        </li>
        <li className={passwordValidation.hasNumber ? 'valid' : 'invalid'}>
          {passwordValidation.hasNumber ? '✓' : '○'} Number
        </li>
        <li className={passwordValidation.hasSpecial ? 'valid' : 'invalid'}>
          {passwordValidation.hasSpecial ? '✓' : '○'} Special character
        </li>
      </ul>
    </div>
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!passwordValidation.isValid) {
      setError('Please ensure your password meets all requirements');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      
      // Incluir código de referido si existe
      const registrationData: any = {
        email: formData.email,
        password: formData.password
      };
      
      if (referralCode) {
        registrationData.referralCode = referralCode;
        console.log('Registering with referral code:', referralCode);
      }
      
      await authService.register(registrationData);
      navigate('/verify-email', { state: { email: formData.email } });
    } catch (err: any) {
      setError(err.message || 'Error during registration');
    } finally {
      setLoading(false);
    }
  };
  const rootStyle = {
    '--bg-image': `url(${fondoPantalla})`
  } as React.CSSProperties;

  return (
    <div style={{ backgroundColor: '#003DA5', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '30px', width: '100%' }}>
        {/* Logo centrado */}
        <div style={{ textAlign: 'center', width: '400px', maxWidth: '90vw' }}>
          <img src={logonegro} alt="Kanz Bank" style={{ width: 'auto', height: '252px', maxWidth: '100%' }} />
        </div>

        {/* Formulario centrado */}
        <form className="auth-form-container" onSubmit={handleSubmit} style={{ width: '400px', maxWidth: '90vw', position: 'relative', zIndex: 10, margin: '0', gridColumn: 'unset' }}>
          {error && (
            <div className="alert alert-danger">
              {error}
            </div>
          )}
          
          {hasReferralCode && (
            <div className="alert alert-info" style={{ 
              backgroundColor: '#f0f8ff', 
              border: '1px solid #B3A369',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              color: '#333'
            }}>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>
                🎁 Referral Code Applied: <strong>{referralCode}</strong>
              </div>
              {referrerName && (
                <div style={{ fontSize: '12px', marginTop: '4px', color: '#666' }}>
                  Referred by: {referrerName}
                </div>
              )}
            </div>
          )}

          <div className="input-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email"
              disabled={loading}
              required
            />
          </div>

          <div className="input-group">
            <label htmlFor="password">Password</label>
            <div className="password-input">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                disabled={loading}
                required
                style={{ width: "80%" }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle"
              >
                {showPassword ? 'Hide' : 'View'}
              </button>
            </div>
            {formData.password && renderPasswordRequirements()}
          </div>

          <div className="input-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="password-input">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm your password"
                disabled={loading}
                required
                style={{ width: "80%" }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="password-toggle"
              >
                {showConfirmPassword ? 'Hide' : 'View'}
              </button>
            </div>
            {!passwordsMatch && formData.confirmPassword && (
              <span className="password-mismatch">Passwords do not match</span>
            )}
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={loading || !passwordsMatch || !passwordValidation.isValid}
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>

          <div className="sm:secondary-buttons flex">
            <button
              type="button"
              className="btn-signup"
              onClick={() => navigate('/verify-email')}
              disabled={loading}
            >
              Validate email
            </button>
            <button
              type="button"
              className="btn-validate"
              onClick={() => navigate('/')}
              disabled={loading}
            >
              Back to Login
            </button>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default SignUpPage;